import React, { useMemo } from 'react';
import { graphql, useFragment } from 'react-relay';
import { type OptionProps, type OptionType, PopupSelect } from '@atlaskit/select';
import { useIntl } from '@atlassian/jira-intl';
import type { reviewersFilter_softwareCodePage$key } from '@atlassian/jira-relay/src/__generated__/reviewersFilter_softwareCodePage.graphql';
import { REVIEWERS_FILTER_BUTTON_TEST_ID } from '../../../../../../../common/constants';
import { FilterButton } from '../../../../../../../common/ui/filter-button';
import { FilterUserCheckbox } from '../../../../../../../common/ui/filter-user-checkbox';
import { UNKNOWN_ID } from '../../../../../../../common/utils/filters/constants';
import { distinctUsers, useDefaultIsOpen } from '../../../../../../../common/utils/filters/utils';
import { usePullRequestTableOptionsStore } from '../../../../../../../controllers/pull-request-filters';
import messages from './messages';

type ReviewersFilterProps = {
	disabled: boolean;
	reviewersFilterData: reviewersFilter_softwareCodePage$key;
};

export const ReviewersFilter = ({ disabled, reviewersFilterData }: ReviewersFilterProps) => {
	const data = useFragment(
		graphql`
			fragment reviewersFilter_softwareCodePage on DevOpsPullRequestDetails @relay(plural: true) {
				reviewers {
					user {
						accountId
						name
						picture
					}
				}
			}
		`,
		reviewersFilterData,
	);

	const { formatMessage } = useIntl();
	const options: OptionType[] = useMemo(
		() =>
			distinctUsers(
				data.flatMap((pullRequest) => pullRequest?.reviewers?.map((reviewer) => reviewer?.user)),
			).map((user) => ({
				label: user?.name ?? formatMessage(messages.unknownUser),
				value: user?.accountId ?? UNKNOWN_ID,
				picture: user?.picture,
			})),
		[data, formatMessage],
	);

	const defaults = {
		options,
		placeholder: formatMessage(messages.searchLabel),
		'aria-label': formatMessage(messages.searchLabel),
		components: {
			Option: (props: OptionProps<OptionType, true>) => <FilterUserCheckbox {...props} />,
		},
		defaultIsOpen: useDefaultIsOpen(),
	};

	const [filterValues, filtersActions] = usePullRequestTableOptionsStore();
	const selectedValues = useMemo(
		() =>
			filterValues.reviewersFilter
				.map((accountId) => options.find((option) => option.value === accountId))
				.filter((option): option is OptionType => option != null),
		[filterValues, options],
	);

	return (
		<PopupSelect
			{...defaults}
			value={selectedValues}
			backspaceRemovesValue
			controlShouldRenderValue
			isClearable
			closeMenuOnSelect={false}
			hideSelectedOptions={false}
			isDisabled={disabled}
			// Temporary strategy to mitigate a PopupSelect regression causing scroll jumps
			popperProps={{ strategy: 'fixed' }}
			isMulti
			classNamePrefix="reviewers-filter"
			onChange={(selectedOptions) =>
				filtersActions.setReviewersFilter(
					selectedOptions
						.map((option) => option.value)
						.filter((value): value is string => typeof value === 'string'),
				)
			}
			target={({ isOpen, ...triggerProps }) => (
				<FilterButton
					{...triggerProps}
					testId={REVIEWERS_FILTER_BUTTON_TEST_ID}
					isSelected={isOpen}
					label={formatMessage(messages.reviewerSelectLabel)}
					selectedCount={filterValues.reviewersFilter.length}
				/>
			)}
		/>
	);
};
